import { Component, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { AuthService } from '@app/_services/auth.service';
import { SettingsService } from '@app/_services/settings.service';
import { RtdbListenerService } from '@app/_services/rtdb-listener.service';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

@Component({
    selector: 'app-blocked-page',
    standalone: true,
    imports: [],
    template: `
        <div class="blocked-container">
            <div class="blocked-content">
                <div class="blocked-icon">
                    <!-- Material warning_amber_rounded — bezier curves for rounded corners, matching Flutter Icons.warning_amber_rounded -->
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5.99L19.53 19H4.47L12 5.99M2.74 18c-.77 1.33.19 3 1.73 3h15.06c1.54 0 2.5-1.67 1.73-3L13.73 4.99c-.77-1.33-2.69-1.33-3.46 0L2.74 18z"/>
                        <path d="M11 11v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1z"/>
                        <path d="M12 17.5c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/>
                    </svg>
                </div>

                @if (isRateLimited()) {
                    <h1>{{ ss.t('rateLimitBlocked_title') }}</h1>
                    <p class="block-message">{{ ss.t('rateLimited_content') }}</p>
                    <div class="countdown">
                        <span class="countdown-time">{{ formattedCountdown() }}</span>
                    </div>
                    <p class="support-text">{{ ss.t('accountSuspended_contactSupport', { supportEmail: BRAND_CONFIG.supportEmail }) }}</p>
                } @else {
                    <h1>{{ isBanned() ? ss.t('accountBanned_title') : ss.t('accountSuspended_title') }}</h1>
                    <p class="block-message">{{ displayMessage() }}</p>
                    <button class="logout-btn" (click)="logout()">{{ ss.t('common_buttonLogOut') }}</button>
                    <p class="support-text">{{ ss.t('accountSuspended_contactSupport', { supportEmail: BRAND_CONFIG.supportEmail }) }}</p>
                }
            </div>
        </div>
    `,
    styles: [`
        .blocked-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: var(--color6);
            padding: 20px;
        }

        .blocked-content {
            text-align: center;
            padding: 40px;
            max-width: 400px;
            margin: 0 auto;
            background: var(--color5);
            border-radius: 16px;
        }

        .blocked-icon {
            display: flex;
            justify-content: center;
            margin-bottom: 24px;
            color: var(--color11);
        }

        h1 {
            color: var(--color8);
            font-size: 22px;
            font-weight: 600;
            margin-bottom: 16px;
        }

        .block-message {
            color: var(--color7);
            font-size: 15px;
            line-height: 1.5;
            margin-bottom: 24px;
        }

        .countdown {
            margin: 24px 0;
        }

        .countdown-time {
            font-size: 48px;
            font-weight: 700;
            color: var(--color11);
            font-variant-numeric: tabular-nums;
        }

        .logout-btn {
            background: var(--color1);
            color: var(--color12);
            border: none;
            height: 44px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            width: 100%;
            transition: opacity 0.2s;
        }

        .logout-btn:hover {
            opacity: 0.9;
        }

        .support-text {
            color: var(--color7);
            font-size: 13px;
            margin-top: 16px;
            margin-bottom: 0;
        }
    `]
})
export class BlockedPageComponent implements OnDestroy {
    private authService = inject(AuthService);
    private rtdbListener = inject(RtdbListenerService);
    ss = inject(SettingsService);
    readonly BRAND_CONFIG = BRAND_CONFIG;

    private countdownInterval: ReturnType<typeof setInterval> | null = null;
    countdownSeconds = signal(0);

    // Reactive: reads directly from RTDB signal, updates in real-time
    isRateLimited = computed(() => this.rtdbListener.accessStatus()?.type === 'rate_limited');
    isBanned = computed(() => this.rtdbListener.accessStatus()?.type === 'banned');

    displayMessage = computed(() => {
        const status = this.rtdbListener.accessStatus();
        if (status?.message) return status.message;
        return status?.type === 'banned'
            ? this.ss.t('accountBanned_defaultMessage')
            : this.ss.t('accountBlocked_defaultMessage');
    });

    formattedCountdown = computed(() => {
        const s = this.countdownSeconds();
        const minutes = Math.floor(s / 60);
        const seconds = s % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    });

    private retryAfterFromRtdb = computed(() => {
        const status = this.rtdbListener.accessStatus();
        return (status?.type === 'rate_limited' && status?.retryAfter) ? status.retryAfter : 0;
    });

    constructor() {
        effect(() => {
            const seconds = this.retryAfterFromRtdb();
            if (seconds > 0) {
                this.startCountdown(seconds);
            } else {
                this.stopCountdown();
            }
        });
    }

    ngOnDestroy(): void {
        this.stopCountdown();
    }

    logout(): void {
        this.authService.logoutUser();
    }

    private startCountdown(seconds: number): void {
        this.stopCountdown();
        this.countdownSeconds.set(seconds);
        this.countdownInterval = setInterval(() => {
            const current = this.countdownSeconds();
            if (current <= 1) {
                this.stopCountdown();
            } else {
                this.countdownSeconds.set(current - 1);
            }
        }, 1000);
    }

    private stopCountdown(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
        this.countdownSeconds.set(0);
    }
}
